import PropTypes from 'prop-types';
import React from 'react';
import AutomationIcon from './AutomationIcon';
import JargonTooltip from '../../shared/components/JargonTooltip';
import Badge from '../../shared/components/Badge';
import ToggleSwitch from '../../shared/components/ToggleSwitch';
import { PAGE_COPY } from '../constants/automationRules';

// The shell every rule on the page wears. A rule that is off collapses to its title, its
// description and its switch: six equally loud fully-expanded cards made it impossible to
// see at a glance what was actually running.
//
// `renderBody` is the expanded body, and is a function rather than `children` so the card is
// the ONE place that decides whether a rule is expanded. Passing an element would build it on
// every render even when collapsed — which also means React validates its propTypes against
// config a collapsed card hasn't got. `footer` always renders: the block-list warning has to
// be readable while the rule is off, which is exactly the state it warns about.
const RuleCard = ({
  ruleType,
  title,
  icon,
  description,
  badge,
  badgeTone,
  tooltip,
  enabled,
  deferred,
  locked,
  busy,
  canAdminister,
  showToggle,
  onToggle,
  footer,
  renderBody,
}) => {
  const titleId = `rule-title-${ruleType}`;
  const bodyId = `rule-body-${ruleType}`;
  const body = enabled && !deferred && renderBody ? renderBody() : null;

  return (
    <div
      className="automation-rules__card"
      role="group"
      aria-labelledby={titleId}
      data-testid={`rule-card-${ruleType}`}
    >
      <div className="automation-rules__card-head">
        {icon ? (
          <span className="automation-rules__card-icon" aria-hidden="true">
            <AutomationIcon name={icon} />
          </span>
        ) : null}
        <div className="automation-rules__card-main">
          <div className="automation-rules__card-titles">
            <span className="automation-rules__card-title" id={titleId}>
              {title}
            </span>
            {tooltip ? (
              <JargonTooltip term={tooltip.term} text={tooltip.text} id={`tip-${ruleType}`} />
            ) : null}
            {badge ? <Badge label={badge} tone={badgeTone} /> : null}
          </div>
          <p className="automation-rules__card-description">{description}</p>
        </div>
        {showToggle ? (
          <div>
            {/* `deferred` means the rule has never been available to this team, so a
                switch would be a lie. `locked` is different: the rule IS armed and
                firing, and only the org-wide kill switch is off — swapping its switch
                for a Coming soon badge there would hide a running rule. Render it,
                disabled, so the state stays visible. */}
            {deferred ? (
              <Badge
                label={PAGE_COPY.comingSoon}
                tone="neutral"
                testId={`rule-coming-soon-${ruleType}`}
              />
            ) : (
              <ToggleSwitch
                on={enabled}
                disabled={locked || !canAdminister || busy}
                onChange={onToggle}
                label={title}
                controls={body ? bodyId : undefined}
                testId={`rule-toggle-${ruleType}`}
              />
            )}
          </div>
        ) : null}
      </div>
      {/* A sibling of the head, not a child of its text column: the divider above the
          threshold row spans the whole card in the design, including under the switch. */}
      {body ? (
        <div className="automation-rules__card-body" id={bodyId}>
          {body}
        </div>
      ) : null}
      {footer}
    </div>
  );
};

RuleCard.propTypes = {
  ruleType: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  icon: PropTypes.string,
  description: PropTypes.string.isRequired,
  badge: PropTypes.string,
  badgeTone: PropTypes.oneOf(['brand', 'neutral']),
  tooltip: PropTypes.shape({ term: PropTypes.string, text: PropTypes.string }),
  enabled: PropTypes.bool,
  deferred: PropTypes.bool,
  locked: PropTypes.bool,
  busy: PropTypes.bool,
  canAdminister: PropTypes.bool,
  showToggle: PropTypes.bool,
  onToggle: PropTypes.func,
  footer: PropTypes.node,
  renderBody: PropTypes.func,
};

RuleCard.defaultProps = {
  icon: null,
  badge: null,
  badgeTone: 'brand',
  tooltip: null,
  enabled: false,
  deferred: false,
  locked: false,
  busy: false,
  canAdminister: false,
  showToggle: true,
  onToggle: () => {},
  footer: null,
  renderBody: null,
};

export default RuleCard;
